import {t} from 'i18next';
import React, {useRef, useEffect} from 'react';
import './ArrFunnelChart.scss';

export interface FunnelSubStage {
  id: string;
  label: string;
  value: number;
  color?: string;
}

const CORNER_RADIUS = 6;
const DEFAULT_STAGE_HEIGHT = 400;
const DEFAULT_STAGE_WIDTH = 400;

interface FunnelStage {
  id: string;
  label: string;
  value: number;
  color?: string;
  width?: number;
  height?: number;
  subStages?: FunnelSubStage[];
}

export interface FunnelWidgetData {
  released: number;
  notReviewed: number;
  reviewed: number;
  responsive: number;
  privileged: number;
  totalProduced: number;
}

interface FunnelLabels {
  released?: string;
  notReviewed?: string;
  reviewed?: string;
  responsive?: string;
  privileged?: string;
  totalProduced?: string;
}

interface StageData {
  stage: FunnelStage;
  index: number;
  y: number;
  width: number;
  height: number;
  x: number;
  centerX: number;
}

interface FunnelChartProps {
  widgetData: FunnelWidgetData;
  labels?: FunnelLabels;
  width?: number;
  height?: number;
  className?: string;
}

const convertWidgetDataToStages = (
  widgetData: FunnelWidgetData,
  labels?: FunnelLabels
): FunnelStage[] => {
  const isEmptyState = Object.values(widgetData).every((value) => value === 0);

  const defaultLabels = {
    released: 'dashboard.endToEndWidget.released',
    notReviewed: 'dashboard.endToEndWidget.notReviewed',
    reviewed: 'dashboard.endToEndWidget.reviewed',
    responsive: 'dashboard.endToEndWidget.responsive',
    privileged: 'dashboard.endToEndWidget.privileged',
    totalProduced: 'dashboard.endToEndWidget.totalProduced',
  };

  const finalLabels = {...defaultLabels, ...labels};

  const colors = {
    empty: {
      released: '#182231',
      subStages: ['#1F2B3D', '#263449', '#1F2B3D', '#263449'],
      totalProduced: '#2D3D55',
    },
    populated: {
      released: '#89A6FF',
      subStages: ['#00D0DB', '#C4AAEB', '#00D0DB', '#C4AAEB'],
      totalProduced: '#7DDFBB',
    },
  }[isEmptyState ? 'empty' : 'populated'];

  const stages = [
    {
      id: '1',
      label: finalLabels.released,
      value: widgetData.released,
      color: colors.released,
      width: 382,
      height: 100,
    },
    {
      id: '2',
      label: '',
      value:
        widgetData.notReviewed +
        widgetData.reviewed +
        widgetData.responsive +
        widgetData.privileged,
      width: 302,
      height: 178,
      subStages: [
        {
          id: '2a',
          label: finalLabels.notReviewed,
          value: widgetData.notReviewed,
          color: colors.subStages[0],
        },
        {
          id: '2b',
          label: finalLabels.responsive,
          value: widgetData.responsive,
          color: colors.subStages[1],
        },
        {
          id: '2c',
          label: finalLabels.reviewed,
          value: widgetData.reviewed,
          color: colors.subStages[2],
        },
        {
          id: '2d',
          label: finalLabels.privileged,
          value: widgetData.privileged,
          color: colors.subStages[3],
        },
      ],
    },
    {
      id: '3',
      label: finalLabels.totalProduced,
      value: widgetData.totalProduced,
      color: colors.totalProduced,
      width: 224,
      height: 100,
    },
  ];

  return stages;
};
function ArrFunnelChart({
  widgetData,
  labels,
  width = 470,
  height = 378,
  className = '',
}: FunnelChartProps) {
  const funnelData = convertWidgetDataToStages(widgetData, labels);
  const canvasRef = useRef(null);
  const internalPadding = {
    horizontal: 5,
    vertical: 10,
  };

  const actualWidth = width;
  let actualHeight = height;

  const totalCustomHeight = funnelData.reduce(
    (sum, stage) => sum + (stage.height || DEFAULT_STAGE_HEIGHT),
    0
  );
  actualHeight = totalCustomHeight;
  const centerX: number = width / 2;

  const maxWidth = width * 0.95;
  const minWidth = width * 0.35;
  const topWidth = maxWidth;
  const bottomWidth = minWidth;

  const getWidthAtY = (y: number) => {
    const progress = y / totalCustomHeight;
    return topWidth - (topWidth - bottomWidth) * progress;
  };

  const stages: StageData[] = funnelData.map((stage, index) => {
    const stageHeight = stage.height || DEFAULT_STAGE_HEIGHT;
    const y = funnelData
      .slice(0, index)
      .reduce(
        (sum, prevStage) => sum + (prevStage.height || DEFAULT_STAGE_HEIGHT),
        0
      );

    const stageWidth = getWidthAtY(y + stageHeight / 2);

    return {
      stage,
      index,
      y,
      width: stageWidth,
      height: stageHeight,
      x: centerX - stageWidth / 2,
      centerX: centerX,
    };
  });

  const drawFunnel = () => {
    const canvas = canvasRef.current as unknown as HTMLCanvasElement;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, actualWidth, actualHeight);

    if (funnelData.length === 0) return;

    const totalCustomHeight = funnelData.reduce(
      (sum, stage) => sum + (stage.height || DEFAULT_STAGE_HEIGHT),
      0
    );
    const maxWidth = Math.max(
      ...funnelData.map((stage) => stage.width || DEFAULT_STAGE_WIDTH)
    );
    const minWidth = Math.min(...funnelData.map((stage) => stage.width || 200));

    const topY = 0;
    const bottomY = totalCustomHeight;
    const topLeftX = centerX - maxWidth / 2;
    const topRightX = centerX + maxWidth / 2;
    const bottomLeftX = centerX - minWidth / 2;
    const bottomRightX = centerX + minWidth / 2;

    const midY = topY + totalCustomHeight / 2;
    const leftMidX =
      (topLeftX + bottomLeftX) / 2 + (maxWidth - minWidth) * 0.28;
    const rightMidX =
      (topRightX + bottomRightX) / 2 - (maxWidth - minWidth) * 0.28;

    stages.forEach((stageData) => {
      ctx.fillStyle = stageData.stage.color || '#666';
      ctx.globalAlpha = 1.0;

      ctx.save();

      ctx.beginPath();
      ctx.rect(0, stageData.y, actualWidth, stageData.height);
      ctx.clip();

      const cornerRadius = CORNER_RADIUS;
      ctx.beginPath();

      ctx.moveTo(topLeftX + cornerRadius, topY);
      ctx.lineTo(topRightX - cornerRadius, topY);
      ctx.quadraticCurveTo(topRightX, topY, topRightX, topY + cornerRadius);

      ctx.quadraticCurveTo(
        rightMidX,
        midY,
        bottomRightX,
        bottomY - cornerRadius
      );
      ctx.quadraticCurveTo(
        bottomRightX,
        bottomY,
        bottomRightX - cornerRadius,
        bottomY
      );

      ctx.lineTo(bottomLeftX + cornerRadius, bottomY);
      ctx.quadraticCurveTo(
        bottomLeftX,
        bottomY,
        bottomLeftX,
        bottomY - cornerRadius
      );

      ctx.quadraticCurveTo(leftMidX, midY, topLeftX, topY + cornerRadius);
      ctx.quadraticCurveTo(topLeftX, topY, topLeftX + cornerRadius, topY);

      ctx.closePath();
      ctx.fill();

      ctx.restore();

      if (stageData.stage.subStages && stageData.stage.subStages.length > 0) {
        const cols = 2;
        const rows = Math.ceil(stageData.stage.subStages.length / cols);
        const subStageWidth = stageData.width / cols;
        const subStageHeight = stageData.height / rows;

        stageData.stage.subStages.forEach((subStage, subIndex) => {
          const col = subIndex % cols;
          const row = Math.floor(subIndex / cols);
          const subX = stageData.x + col * subStageWidth;

          const subY = stageData.y + row * subStageHeight;

          ctx.fillStyle = subStage.color || stageData.stage.color || '#263449';
          ctx.globalAlpha = 1.0;

          ctx.save();

          ctx.beginPath();
          ctx.rect(subX, subY, subStageWidth, subStageHeight);
          ctx.clip();

          const cornerRadius = CORNER_RADIUS;
          ctx.beginPath();

          ctx.moveTo(topLeftX + cornerRadius, topY);
          ctx.lineTo(topRightX - cornerRadius, topY);
          ctx.quadraticCurveTo(topRightX, topY, topRightX, topY + cornerRadius);

          ctx.quadraticCurveTo(
            rightMidX,
            midY,
            bottomRightX,
            bottomY - cornerRadius
          );
          ctx.quadraticCurveTo(
            bottomRightX,
            bottomY,
            bottomRightX - cornerRadius,
            bottomY
          );

          ctx.lineTo(bottomLeftX + cornerRadius, bottomY);
          ctx.quadraticCurveTo(
            bottomLeftX,
            bottomY,
            bottomLeftX,
            bottomY - cornerRadius
          );

          ctx.quadraticCurveTo(leftMidX, midY, topLeftX, topY + cornerRadius);
          ctx.quadraticCurveTo(topLeftX, topY, topLeftX + cornerRadius, topY);

          ctx.closePath();
          ctx.fill();

          ctx.restore();

          const isDarkBackground =
            subStage.color === '#1F2B3D' || subStage.color === '#263449';
          const labelColor = isDarkBackground ? '#FCFCFD' : '#0C1119';
          const labelStyle = getTextStyle('sub', 'label');

          ctx.fillStyle = labelColor;
          ctx.font = `${labelStyle.fontWeight} ${labelStyle.fontSize} ${labelStyle.fontFamily}`;

          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';

          const textX = subX + subStageWidth / 2;
          const textY = subY + subStageHeight / 2;

          const paddedTextY = textY + internalPadding.vertical;

          if (subIndex === 0) {
            ctx.fillStyle = labelColor;
            ctx.font = `${labelStyle.fontWeight} ${labelStyle.fontSize} ${labelStyle.fontFamily}`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(t(subStage.label), textX + 10, paddedTextY - 10);

            ctx.fillText(
              subStage.value.toLocaleString(),
              textX + 10,
              paddedTextY + 12
            );
          }
          if (subIndex === 1) {
            ctx.fillStyle = labelColor;
            ctx.font = `${labelStyle.fontWeight} ${labelStyle.fontSize} ${labelStyle.fontFamily}`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(t(subStage.label), textX - 15, paddedTextY - 10);

            ctx.fillText(
              subStage.value.toLocaleString(),
              textX - 20,
              paddedTextY + 10
            );
          }
          if (subIndex === 2) {
            ctx.fillStyle = labelColor;
            ctx.font = `${labelStyle.fontWeight} ${labelStyle.fontSize} ${labelStyle.fontFamily}`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(t(subStage.label), textX + 15, paddedTextY - 40);

            ctx.fillText(
              subStage.value.toLocaleString(),
              textX + 15,
              paddedTextY - 20
            );
          }
          if (subIndex === 3) {
            ctx.fillStyle = labelColor;
            ctx.font = `${labelStyle.fontWeight} ${labelStyle.fontSize} ${labelStyle.fontFamily}`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(t(subStage.label), textX - 15, paddedTextY - 40);

            ctx.fillText(
              subStage.value.toLocaleString(),
              textX - 20,
              paddedTextY - 20
            );
          }
        });
      } else {
        const isDarkBackground =
          stageData.stage.color === '#182231' ||
          stageData.stage.color === '#2D3D55';
        const labelColor = isDarkBackground ? '#FCFCFD' : '#0C1119';
        const valueColor = isDarkBackground ? '#FCFCFD' : '#0C1119';
        const labelStyle = getTextStyle('main', 'label');
        const valueStyle = getTextStyle('main', 'value');

        ctx.fillStyle = labelColor;
        ctx.font = `${labelStyle.fontWeight} ${labelStyle.fontSize} ${labelStyle.fontFamily}`;

        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        ctx.fillText(
          t(stageData.stage.label),
          centerX,
          stageData.y + stageData.height / 2 - 20
        );

        ctx.font = `${valueStyle.fontWeight} ${valueStyle.fontSize} ${valueStyle.fontFamily}`;

        ctx.fillStyle = valueColor;
        ctx.fillText(
          stageData.stage.value.toLocaleString(),
          centerX,
          stageData.y + stageData.height / 2 + 12
        );
      }
    });

    stages.forEach((stageData, index) => {
      if (index < stages.length - 1) {
        ctx.save();

        const cornerRadius = CORNER_RADIUS;
        ctx.beginPath();

        ctx.moveTo(topLeftX + cornerRadius, topY);
        ctx.lineTo(topRightX - cornerRadius, topY);
        ctx.quadraticCurveTo(topRightX, topY, topRightX, topY + cornerRadius);

        ctx.quadraticCurveTo(
          rightMidX,
          midY,
          bottomRightX,
          bottomY - cornerRadius
        );
        ctx.quadraticCurveTo(
          bottomRightX,
          bottomY,
          bottomRightX - cornerRadius,
          bottomY
        );

        ctx.lineTo(bottomLeftX + cornerRadius, bottomY);
        ctx.quadraticCurveTo(
          bottomLeftX,
          bottomY,
          bottomLeftX,
          bottomY - cornerRadius
        );

        ctx.quadraticCurveTo(leftMidX, midY, topLeftX, topY + cornerRadius);
        ctx.quadraticCurveTo(topLeftX, topY, topLeftX + cornerRadius, topY);

        ctx.closePath();
        ctx.clip();

        ctx.strokeStyle = '#263449';
        ctx.lineWidth = 1;

        const currentY = stageData.y + stageData.height;
        const progress = currentY / totalCustomHeight;
        const currentWidth = maxWidth - (maxWidth - minWidth) * progress;
        const currentLeftX = centerX - currentWidth / 2;
        const currentRightX = centerX + currentWidth / 2;

        ctx.beginPath();
        ctx.moveTo(currentLeftX, currentY);
        ctx.lineTo(currentRightX, currentY);
        ctx.stroke();

        ctx.restore();
      }
    });
  };

  useEffect(() => {
    drawFunnel();
  }, [funnelData]);

  if (funnelData.length === 0) return null;

  const getTextStyle = (
    stageType: 'main' | 'sub',
    textType: 'label' | 'value'
  ) => {
    if (textType === 'label') {
      return {fontFamily: 'Inter', fontSize: '12px', fontWeight: 600};
    }

    if (textType === 'value') {
      if (stageType === 'sub') {
        return {fontFamily: 'Inter', fontSize: '16px', fontWeight: 600};
      } else {
        return {fontFamily: 'Reckless', fontSize: '32px', fontWeight: 400};
      }
    }

    return {fontFamily: 'Inter', fontSize: '12px', fontWeight: 400};
  };

  const isEmptyState = widgetData
    ? Object.values(widgetData).every((value) => value === 0)
    : false;

  return (
    <div
      className={`funnel-chart-container ${className} ${isEmptyState ? 'empty-state' : 'populated-state'}`}
      data-testid={`arr-funnel-chart-div`}>
      <canvas
        ref={canvasRef}
        width={actualWidth}
        height={actualHeight}
        className="funnel-canvas"
        data-testid="funnel-chart-canvas"
      />
    </div>
  );
}

export default ArrFunnelChart;
